import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '../hooks/useAnalytics';
import { HeroSection } from '../components/HeroSection';
import { ProblemSection } from '../components/ProblemSection';
import { SolutionSection } from '../components/SolutionSection';
import { PolicyModal } from '../components/PolicyModal';
import { SalesNotification } from '../components/SalesNotification';
// Lazy load below-the-fold components for faster initial load
const MechanismSection = lazy(() => import('../components/MechanismSection').then(m => ({
  default: m.MechanismSection
})));
const BenefitsSection = lazy(() => import('../components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
})));
const UseCasesSection = lazy(() => import('../components/UseCasesSection').then(m => ({
  default: m.UseCasesSection
})));
const HowToUseSection = lazy(() => import('../components/HowToUseSection').then(m => ({
  default: m.HowToUseSection
})));
const TrustSection = lazy(() => import('../components/TrustSection').then(m => ({
  default: m.TrustSection
})));
const BundleSection = lazy(() => import('../components/BundleSection').then(m => ({
  default: m.BundleSection
})));
const FAQSection = lazy(() => import('../components/FAQSection').then(m => ({
  default: m.FAQSection
})));
const CTASection = lazy(() => import('../components/CTASection').then(m => ({
  default: m.CTASection
})));
type PolicyType = 'privacy' | 'terms' | 'refund';
// Loading fallback for lazy sections
function SectionLoader() {
  return <div className="w-full py-12 flex items-center justify-center" data-id="element-1618">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ginger" data-id="element-1619"></div>
    </div>;
}
const navigationLinks = [{
  name: 'How It Works',
  href: '#how-it-works'
}, {
  name: 'Benefits',
  href: '#benefits'
}, {
  name: 'How To Use',
  href: '#how-to-use'
}, {
  name: 'Pricing',
  href: '#bundles'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function GinextLanding() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    trackPageView
  } = useAnalytics();
  // Track page view on mount
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);
  // Handle scrolling to hash on mount and when hash changes
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    // Small delay to allow mobile menu to close before scrolling
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        // Calculate the position accounting for fixed header
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <main className="w-full min-h-screen bg-bone overflow-x-hidden" data-id="element-1620">
      {/* Header */}
      <header className="bg-white md:bg-white/95 md:backdrop-blur-sm border-b border-bone-dark fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-1621">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-1622">
          <Link to="/" className="flex items-center" data-id="element-1623">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" fetchpriority="high" width="150" height="24" data-id="element-1624" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-1625">
            {navigationLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-light text-charcoal hover:text-ginger transition-colors cursor-pointer" data-id="element-1626">
                {link.name}
              </a>)}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-charcoal hover:text-ginger transition-colors" aria-label="Toggle menu" data-id="element-1627">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-1628" /> : <Menu className="w-6 h-6" data-id="element-1629" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-1630">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden overflow-hidden border-t border-bone-dark bg-white" data-id="element-1631">
              <nav className="px-4 py-4 space-y-1" data-id="element-1632">
                {navigationLinks.map((link, index) => <motion.div key={link.name} data-id="element-1633">
                    <a href={link.href} onClick={e => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick(link.href);
              }} className="block py-3 px-4 text-base font-light text-charcoal hover:text-ginger hover:bg-bone/50 rounded-lg transition-all cursor-pointer" data-id="element-1634">
                      {link.name}
                    </a>
                  </motion.div>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-[57px]" data-id="element-1635">
        <HeroSection data-id="element-1636" />
        <ProblemSection data-id="element-1637" />
        <SolutionSection data-id="element-1638" />

        <Suspense fallback={<SectionLoader data-id="element-1640" />} data-id="element-1639">
          <div id="how-it-works" className="scroll-mt-20" data-id="element-1641">
            <MechanismSection data-id="element-1642" />
          </div>
          <div id="benefits" className="scroll-mt-20" data-id="element-1643">
            <BenefitsSection data-id="element-1644" />
          </div>
          <UseCasesSection data-id="element-1645" />
          <div id="how-to-use" className="scroll-mt-20" data-id="element-1646">
            <HowToUseSection data-id="element-1647" />
          </div>
          <TrustSection data-id="element-1648" />
          <div id="bundles" className="scroll-mt-20" data-id="element-1649">
            <BundleSection data-id="element-1650" />
          </div>
          <div id="faq" className="scroll-mt-20" data-id="element-1651">
            <FAQSection data-id="element-1652" />
          </div>
          <CTASection data-id="element-1653" />
        </Suspense>
      </div>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-1654">
        <div className="max-w-6xl mx-auto px-4" data-id="element-1655">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-1656">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-1657">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-1658">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-1659">
              Refund Policy
            </button>
          </div>
          <p data-id="element-1660">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-1661">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-1662" />

      {/* Sales Notification */}
      <SalesNotification data-id="element-1663" />
    </main>;
}